<template>
  <b-modal
    ref="modal"
    hide-footer
    centered
    title="Create user"
    size="lg"
  >
    <b-form @submit.prevent="save">
      <b-form-group label="Dashboard:">
        <b-form-select
          :options="availableDashboards"
          v-model="$v.selectedDashboard.$model"
          :class="{ 'is-invalid': $v.selectedDashboard.$error }"
          :disabled="inProcess"
        >
        </b-form-select>
        <b-form-invalid-feedback
          v-if="!$v.selectedDashboard.required"
          >
          Dashboard is required
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Role:">
        <b-form-select
          :options="availableRoles"
          v-model="$v.selectedRoles.$model"
          :class="{ 'is-invalid': $v.selectedRoles.$error }"
          :disabled="inProcess"
          multiple
        >
        </b-form-select>
        <b-form-invalid-feedback
          v-if="!$v.selectedRoles.isSelected"
          >
          At least one role must be specified
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="E-mail:">
        <b-form-input
          v-model="$v.email.$model"
          :class="{ 'is-invalid': $v.email.$error }"
          placeholder="example@domain.com"
          :disabled="inProcess"
        />
        <b-form-invalid-feedback
          v-if="!$v.email.required"
          >
          E-mail is required
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.email.email"
          >
          This field should contain a valid E-mail address
        </b-form-invalid-feedback>
        <b-form-invalid-feedback
          v-if="!$v.email.isUnique"
          >
          This E-mail address already registered
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Password:" v-if="!autoPass">
        <b-form-input
          v-model="$v.pass.$model"
          :class="{ 'is-invalid': $v.pass.$error }"
          :disabled="inProcess"
        />
        <b-form-invalid-feedback
          v-if="!$v.pass.required"
          >
          Password is required
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          v-model="autoPass"
          :disabled="inProcess"
        >
          Generate password
        </b-form-checkbox>
      </b-form-group>
      <b-form-group>
        <b-form-checkbox
          v-model="invite"
          :disabled="inProcess"
        >
          Send invitation
        </b-form-checkbox>
      </b-form-group>
      <div>
        <b-button
          variant="primary"
          type="submit"
          :disabled="inProcess"
        >
          <b-spinner v-if="inProcess" small></b-spinner>
          <span>Save</span>
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import { required, email } from 'vuelidate/lib/validators'
import { find, sortBy } from 'lodash'
// helpers
import eventBus from '@/helpers/event-bus'
// services
import db from '@/services/db'
import auth from '@/services/auth'
import user from '@/services/user'

export default {
  props: ['users', 'dashboards'],
  data () {
    return {
      email: null,
      autoPass: true,
      pass: null,
      invite: true,
      selectedDashboard: null,
      availableRoles: [
        { value: 'read', text: 'Regular' },
        { value: 'update', text: 'Dashboard editor' },
        { value: 'manageUsers', text: 'User manager' }
      ],
      selectedRoles: ['read'],
      inProcess: false
    }
  },
  computed: {
    availableDashboards () {
      const items = []

      for (let dId in this.dashboards) {
        const dashboard = this.dashboards[dId]

        if (!dashboard) {
          console.error(`Dashboard with id "${dId}" not found`)
          continue
        }

        const owner = find(this.users, { uid: dashboard.owner })

        items.push({
          value: dId,
          text: `${dashboard.name} (owner: ${owner ? owner.email : dashboard.owner})`,
        })
      }

      return sortBy(items, ['text'])
    },
    userEmails () {
      const emails = []

      for (let user of this.users) {
        if (!user.email) continue

        emails.push(user.email.toLowerCase())
      }

      return emails
    }
  },
  methods: {
    open () {
      if (this.$refs['modal']) this.$refs['modal'].show()
    },
    close () {
      if (this.$refs['modal']) this.$refs['modal'].hide()
    },
    async save () {
      if (this.inProcess) return
      this.$v.$touch()
      if (this.$v.$invalid) return
      this.inProcess = true

      const idToken = await auth.getIdToken()
      const rules = {}

      for (let rule of this.selectedRoles) {
        rules[rule] = true
      }

      const vm = this
      const data = {
        email: vm.email,
        idToken,
        invite: vm.invite,
        dashboard: vm.selectedDashboard,
        rules
      }

      if (this.autoPass) data.pass = this.genPass(6)
      else data.pass = this.pass

      user.create(data)
        .then(() => {
          this.$bvToast.toast(`New user have been created "${this.email}".`, {
            title: 'Success!',
            variant: 'success',
            solid: true,
            toaster: 'b-toaster-top-center',
            autoHideDelay: 2500,
          })

          this.inProcess = false
          this.email = null
          this.selectedRoles = ['read']
          this.selectedDashboard = null
          this.pass = null
          this.autoPass = true
          this.invite = true

          this.$nextTick(() => {
            this.$v.$reset()
          })
          this.close()
          this.$emit('created')
        })
        .catch((err) => {
          this.inProcess = false
          console.error(err)
        })
    },
    genPass (length) {
      var text = ''
      var possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

      for (var i = 0; i < length; i++) {
        text += possible.charAt(Math.floor(Math.random() * possible.length))
      }

      return text
    }
  },
  validations: {
    selectedDashboard: {
      required
    },
    selectedRoles: {
      isSelected (val) {
        if (!val.length) return false
        return true
      }
    },
    email: {
      required,
      email,
      isUnique (val) {
        if (!this.users || !val) return true
        if (this.userEmails.includes(val.toLowerCase())) return false
        return true
      }
    },
    pass: {
      required (val) {
        if (this.autoPass) return true
        if (val && val.length) return true
        return false
      }
    }
  }
}
</script>
